// We use the same environment for development and staging

import {
  DeribitEndpointEnum,
  AdminEndpointEnum,
  BinanceEndpointEnum,
  BybitEndpointEnum,
  HyperliquidEndpointEnum,
  LighterEndpointEnum,
  AirtableEndpointEnum,
  PricerEndpointEnum,
  MMTradesEndpointEnum,
  FilledTradesEndpointEnum,
  IntercomEndpointEnum,
  MMMonitoringEndpointEnum,
  TradingParametersEndpointEnum,
  WithdrawalsEndpointEnum,
} from "../enums/endpoints";

// But we still need a switch to only show dev features locally
export const isStaging = () =>
  process.env.REACT_APP_VERCEL_GIT_COMMIT_REF === "testnet";
export const isProduction = () =>
  process.env.REACT_APP_VERCEL_GIT_COMMIT_REF === "master";
export const isDevelopment = () => !isStaging() && !isProduction();

// Fail closed for an unset/unknown environment; server authorization is still required.
export const isAffiliateTestEnvironment = () =>
  ["testnet", "local"].includes(process.env.REACT_APP_ENVIRONMENT || "");

export const isAffiliateEnvironment = () =>
  isAffiliateTestEnvironment() ||
  (process.env.REACT_APP_ENVIRONMENT === "mainnet" &&
    process.env.REACT_APP_AFFILIATE_PRODUCTION_ENABLED === "true");

export const CLIENT_ID = () => {
  let client_id: string = "";
  switch (process.env.REACT_APP_ENVIRONMENT) {
    case "testnet": {
      client_id = process.env.REACT_APP_ADMIN_CLIENT_ID_TESTNET as string;
      break;
    }
    case "local": {
      client_id = process.env.REACT_APP_ADMIN_CLIENT_ID_TESTNET as string;
      break;
    }
    case "mainnet": {
      client_id = process.env.REACT_APP_ADMIN_CLIENT_ID_MAINNET as string;
      break;
    }

    default:
      break;
  }
  return client_id;
};

export const DERIBIT_API_URL = (endpoint: DeribitEndpointEnum) =>
  `${process.env.REACT_APP_AEVO_API_URL_MAINNET}/api/deribit/${endpoint}`;
export const BINANCE_API_URL = (endpoint: BinanceEndpointEnum) =>
  `${process.env.REACT_APP_AEVO_API_URL_MAINNET}/api/binance/${endpoint}`;
export const BYBIT_API_URL = (endpoint: BybitEndpointEnum) =>
  `${process.env.REACT_APP_AEVO_API_URL_MAINNET}/api/bybit/${endpoint}`;
export const HYPERLIQUID_API_URL = (endpoint: HyperliquidEndpointEnum) =>
  `${process.env.REACT_APP_AEVO_API_URL_MAINNET}/api/hyperliquid/${endpoint}`;
export const LIGHTER_API_URL = (endpoint: LighterEndpointEnum) =>
  `${process.env.REACT_APP_AEVO_API_URL_MAINNET}/api/lighter/${endpoint}`;
export const AIRTABLE_API_URL = (endpoint: AirtableEndpointEnum) =>
  `${process.env.REACT_APP_AEVO_API_URL_MAINNET}/api/airtable/${endpoint}`;
export const PRICER_API_URL = (endpoint: PricerEndpointEnum) =>
  `${process.env.REACT_APP_AEVO_API_URL_MAINNET}/api/pricer/${endpoint}`;
export const MMTRADES_API_URL = (endpoint: MMTradesEndpointEnum) =>
  `${process.env.REACT_APP_AEVO_API_URL_MAINNET}/api/mm/${endpoint}`;
export const TRADES_API_URL = (endpoint: FilledTradesEndpointEnum) =>
  `${
    process.env.REACT_APP_ENVIRONMENT === "testnet"
      ? process.env.REACT_APP_AEVO_API_URL_TESTNET
      : process.env.REACT_APP_AEVO_API_URL_MAINNET
  }/api/trades/${endpoint}`;
export const INTERCOM_API_URL = (endpoint: IntercomEndpointEnum) =>
  `${process.env.REACT_APP_AEVO_API_URL_MAINNET}/api/intercom/${endpoint}`;
export const MM_MONITORING_API_URL = (endpoint: MMMonitoringEndpointEnum) =>
  `${process.env.REACT_APP_AEVO_API_URL_MAINNET}/api/mm-process-control/${endpoint}`;
export const TRADING_PARAMETERS_API_URL = (
  endpoint: TradingParametersEndpointEnum
) =>
  `${process.env.REACT_APP_AEVO_API_URL_MAINNET}/api/trading-parameters/${endpoint}`;
export const WITHDRAWALS_API_URL = (endpoint: WithdrawalsEndpointEnum) =>
  `${process.env.REACT_APP_AEVO_API_URL_MAINNET}/api/withdrawals/${endpoint}`;

// Public exchange web app (used for partner-facing links such as builder
// approval). The testnet domain matches the trusted CORS origin in
// exchange-backend config.CorsOrigin(). Local admin uses testnet
// credentials, so it links to the testnet app. Returns undefined for an
// unknown environment so callers can refuse to build links.
export const EXCHANGE_APP_URL = (): string | undefined => {
  switch (process.env.REACT_APP_ENVIRONMENT) {
    case "mainnet":
      return "https://app.aevo.xyz";
    case "testnet":
    case "local":
      return "https://testnet.aevo.xyz";
    default:
      return undefined;
  }
};

export const ADMIN_API_URL = (endpoint: AdminEndpointEnum | string) => {
  let url: string = "";
  switch (process.env.REACT_APP_ENVIRONMENT) {
    case "testnet": {
      url = `${process.env.REACT_APP_ADMIN_API_URL_TESTNET}/${endpoint}`;
      break;
    }
    case "mainnet": {
      url = `${process.env.REACT_APP_ADMIN_API_URL_MAINNET}/${endpoint}`;
      break;
    }
    case "local": {
      url = `${process.env.REACT_APP_ADMIN_API_URL_LOCAL}/${endpoint}`;
      break;
    }

    default:
      break;
  }
  return url;
};

export const AEVO_API_URL = (endpoint: AdminEndpointEnum | string) => {
  let url: string = "";
  switch (process.env.REACT_APP_ENVIRONMENT) {
    case "testnet": {
      url = `https://api-testnet.aevo.xyz/${endpoint}`;
      break;
    }
    case "mainnet": {
      url = `https://api.aevo.xyz/${endpoint}`;
      break;
    }

    default:
      break;
  }
  return url;
};

// degen api
export const DEGEN_API_URL = () => {
  let url: string = "";
  switch (process.env.REACT_APP_ENVIRONMENT) {
    case "testnet": {
      url = "https://api-ribbon.vercel.app/api/degen-tx";
      break;
    }
    case "mainnet": {
      url = "https://api-ribbon.vercel.app/api/degen-tx";
      break;
    }
    case "local": {
      url = "https://api-ribbon.vercel.app/api/degen-tx";
      break;
    }

    default:
      url = "http://localhost:4001/api/degen-tx";
      break;
  }
  return url;
};
